import { Control, Controller, FormState, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { NewRequestTableRow } from './NewRequestTableRow';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {Client} from '../../models/table-models';
import Select from "react-select";
import {optionSelectStyles} from "../../styles/selectStyles";
import { EmailChipInput } from '../EmailChipInput';
import { getInvoiceTextTemplates, InvoiceTextTemplate } from '../../utils/invoice-text-templates';
import { getCompanyVatLookup, getPersonVatLookup, VatLookupResult } from '../../utils/clients-hooks';
import { digitsOnly, isValidBulstat, isValidPersonIdentifier } from '../../utils/client-identity';

interface ClientSubFormProps {
    control: Control<Client>;
    watch: UseFormWatch<Client>;
    disabled: boolean;
    clientVatPrefix: string;
    editForm?: boolean;
    formState: FormState<Client>;
    setValue: UseFormSetValue<Client>;
}

export const ClientSubForm = ({
    control,
    watch,
    disabled,
    clientVatPrefix,
    editForm,
    formState,
    setValue,
}: ClientSubFormProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: templateT } = useTranslation('translation', { keyPrefix: 'invoiceTextTemplates' });
    const { t: registryT } = useTranslation('translation', { keyPrefix: 'registryCheck' });

    const eik = watch('eik');
    const ddc = watch('ddc');
    const isCompany = !!watch('company');
    const hasEik = !!(eik && eik.trim().length > 0);
    // Физическо лице може да е регистрирано по ДДС само с ЕГН/ЛНЧ/9-цифрен БУЛСТАТ; без него отметката е скрита.
    const personIdValid = !isCompany && isValidPersonIdentifier(eik);
    // Идентификаторът, по който се пита VIES: ЕИК на фирма или ЕГН/ЛНЧ/БУЛСТАТ на физическо лице.
    const vatCheckId = isCompany ? (isValidBulstat(eik) ? digitsOnly(eik) : null) : (personIdValid ? digitsOnly(eik) : null);
    const [vatLookup, setVatLookup] = React.useState<VatLookupResult | 'loading' | null>(null);
    // 9 цифри на фирма от Търговския регистър не са БУЛСТАТ на физическо лице - клиентът се въвежда като фирма.
    const personEikCompany = !isCompany && vatLookup && vatLookup !== 'loading' ? vatLookup.companyName : null;
    const canBeVatRegistered = isCompany ? hasEik : personIdValid && !personEikCompany;
    const isDdcDisabled = disabled || !canBeVatRegistered;

    React.useEffect(() => {
        if (!canBeVatRegistered && ddc) {
            setValue('ddc', false);
            setValue('vat', '', { shouldValidate: true });
        }
    }, [canBeVatRegistered, ddc, setValue]);

    // Проверка във VIES при редакция. Отметката се сменя автоматично само когато ЕИК/ЕГН-то или видът
    // (фирма/физическо лице) са променени сега - запазеното решение на оператора не се пипа при отваряне.
    const identityDirty = !!formState.dirtyFields?.eik || !!formState.dirtyFields?.company;
    const identityDirtyRef = React.useRef(identityDirty);
    identityDirtyRef.current = identityDirty;
    React.useEffect(() => {
        setVatLookup(null);
        if (!vatCheckId || disabled) return;
        let active = true;
        const timer = setTimeout(() => {
            setVatLookup('loading');
            (isCompany ? getCompanyVatLookup(vatCheckId) : getPersonVatLookup(vatCheckId))
                .then(result => {
                    if (!active) return;
                    setVatLookup(result);
                    if (identityDirtyRef.current && result.vatRegistered != null) {
                        setValue('ddc', result.vatRegistered);
                        setValue('vat', result.vatRegistered ? (result.vat ?? '').replace(/^BG/i, '') : '',
                            { shouldValidate: true });
                    }
                })
                .catch(() => {
                    if (active) setVatLookup({ vatStatus: 'UNAVAILABLE', vatRegistered: null });
                });
        }, 500);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [vatCheckId, isCompany, disabled, setValue]);
    const vatLookupText = personEikCompany
        ? registryT('personEikIsCompany', { name: personEikCompany })
        : vatLookup === 'loading'
        ? registryT('vatChecking')
        : vatLookup?.vatRegistered === true
            ? `${registryT('vatValid')} (${vatLookup.vat})`
            : vatLookup?.vatRegistered === false
                ? registryT('vatInvalid')
                : vatLookup ? registryT('vatUnavailable') : null;

    const [templates, setTemplates] = React.useState<InvoiceTextTemplate[]>([]);
    const [templatesError, setTemplatesError] = React.useState(false);

    React.useEffect(() => {
        let active = true;
        getInvoiceTextTemplates()
            .then(items => {
                if (active) setTemplates(items);
            })
            .catch(() => {
                if (active) setTemplatesError(true);
            });
        return () => { active = false; };
    }, []);

    const selectedId = watch('invoiceTextTemplateId') || watch('invoiceText') || 'branchTax';
    const selectedTemplate = templates.find(item => item.id === selectedId);
    const invoiceTextOptions = templates.map(item => ({ value: item.id, label: item.name }));

    return (
        <>
            {watch('company') && (
                <NewRequestTableRow label={t('city')} id='city' editForm={editForm} required={formState.errors.city}>
                    <Controller
                        name='city'
                        {...{ control }}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Form.Control
                                type='text'
                                value={field.value ?? ''}
                                ref={field.ref}
                                name={field.name}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                            />
                        )}
                    ></Controller>
                </NewRequestTableRow>
            )}
            {watch('company') && (
                <NewRequestTableRow
                    label={t('address')}
                    id='companyAdress'
                    editForm={editForm}
                    required={formState.errors.companyAddress}
                >
                    <Controller
                        name='companyAddress'
                        {...{ control }}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Form.Control
                                type='text'
                                value={field.value ?? ''}
                                ref={field.ref}
                                name={field.name}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                            />
                        )}
                    ></Controller>
                </NewRequestTableRow>
            )}
            {watch('company') && (
                <NewRequestTableRow label={t('owner')} id='owner' editForm={editForm} required={formState.errors.owner}>
                    <Controller
                        name='owner'
                        {...{ control }}
                        rules={{ required: false }}
                        render={({ field }) => (
                            <Form.Control
                                type='text'
                                value={field.value ?? ''}
                                ref={field.ref}
                                name={field.name}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                            />
                        )}
                    ></Controller>
                </NewRequestTableRow>
            )}
            <NewRequestTableRow label={t('phone')} id='phone' editForm={editForm}>
                <Controller
                    name='phone'
                    {...{ control }}
                    render={({ field }) => (
                        <Form.Control
                            type='text'
                            value={field.value ?? ''}
                            ref={field.ref}
                            name={field.name}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                        />
                    )}
                ></Controller>
            </NewRequestTableRow>
            <NewRequestTableRow
                label={watch('company') ? t('eik') : t('egn')}
                id='eik'
                editForm={editForm}
                required={formState.errors.eik}
            >
                <Controller
                    name='eik'
                    {...{ control }}
                    rules={{ required: watch('company') }}
                    render={({ field }) => (
                        <Form.Control
                            type='text'
                            value={field.value ?? ''}
                            ref={field.ref}
                            name={field.name}
                            onChange={(e) => {
                                const newEik = e.target.value;
                                field.onChange(newEik);
                                if (watch('ddc')) {
                                    if (newEik && newEik.trim().length > 0) {
                                        setValue('vat', newEik, { shouldValidate: true });
                                    } else {
                                        setValue('ddc', false);
                                        setValue('vat', '', { shouldValidate: true });
                                    }
                                }
                            }}
                            onBlur={field.onBlur}
                            disabled={disabled}
                        />
                    )}
                ></Controller>
            </NewRequestTableRow>
            {(isCompany || (personIdValid && !personEikCompany)) && (
                <NewRequestTableRow
                    label={t('ddc')}
                    id='ddc'
                    inputContainerClass='justify-content-center'
                    editForm={editForm}
                >
                    <Controller
                        name='ddc'
                        {...{ control }}
                        render={({ field }) => (
                            <div className='d-flex w-100 justify-content-center gap-4'>
                                <Form.Check
                                    id='client-ddc-yes'
                                    className='p-0 d-flex align-items-center gap-2 mb-0'
                                >
                                    <Form.Check.Input
                                        id='client-ddc-yes'
                                        type='radio'
                                        name='ddc'
                                        className='m-0'
                                        checked={!!field.value}
                                        onChange={() => {
                                            field.onChange(true);
                                            // Copy EIK to VAT; за ФЛ ДДС номерът е ЕГН-то/БУЛСТАТ-ът
                                            setValue('vat', isCompany ? watch('eik') || '' : digitsOnly(watch('eik')), { shouldValidate: true });
                                        }}
                                        disabled={isDdcDisabled}
                                    />
                                    <Form.Check.Label
                                        htmlFor='client-ddc-yes'
                                        className='mb-0'
                                        style={{ cursor: isDdcDisabled ? 'default' : 'pointer' }}
                                    >
                                        {t('yes')}
                                    </Form.Check.Label>
                                </Form.Check>
                                <Form.Check
                                    id='client-ddc-no'
                                    className='p-0 d-flex align-items-center gap-2 mb-0'
                                >
                                    <Form.Check.Input
                                        id='client-ddc-no'
                                        type='radio'
                                        name='ddc'
                                        className='m-0'
                                        checked={!field.value}
                                        onChange={() => {
                                            field.onChange(false);
                                            setValue('vat', '', { shouldValidate: true }); // Clear VAT when DDC is unchecked
                                        }}
                                        disabled={isDdcDisabled}
                                    />
                                    <Form.Check.Label
                                        htmlFor='client-ddc-no'
                                        className='mb-0'
                                        style={{ cursor: isDdcDisabled ? 'default' : 'pointer' }}
                                    >
                                        {t('no')}
                                    </Form.Check.Label>
                                </Form.Check>
                            </div>
                        )}
                    ></Controller>
                </NewRequestTableRow>
            )}
            {vatCheckId && vatLookupText && (
                <tr className='d-flex w-100 border-top-0'>
                    <td colSpan={2} className='w-100 py-0 border-top-0'>
                        <small className={personEikCompany ? 'text-danger' : 'text-muted'}>{vatLookupText}</small>
                    </td>
                </tr>
            )}
            {watch('ddc') && watch('company') && (
                <>
                <NewRequestTableRow label={t('vat')} id='vat' editForm={editForm} hideBottomBorder={!!formState.errors.vat}>
                    <Controller
                        name='vat'
                        {...{ control }}
                        rules={{ validate: value => !watch('company') || !watch('ddc') || /^[0-9]{9,10}$/.test(String(value ?? '').trim())
                            || String(registryT('invalidVatFormat')) }}
                        render={({ field, fieldState }) => (
                            <>
                                <p className='mb-0 mx-1'>{clientVatPrefix}</p>
                                <div className='flex-grow-1'>
                                    <Form.Control
                                        type='text'
                                        inputMode='numeric'
                                        autoComplete='off'
                                        isInvalid={!!fieldState.error}
                                        aria-describedby={fieldState.error ? 'vat-error' : undefined}
                                        value={field.value ?? ''}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        onBlur={field.onBlur}
                                        disabled={disabled}
                                    />
                                </div>
                            </>
                        )}
                    ></Controller>
                </NewRequestTableRow>
                {formState.errors.vat && (
                    <tr className='d-flex w-100 border-top-0'>
                        <td colSpan={2} className='w-100 py-0 border-top-0'>
                            <Form.Control.Feedback id='vat-error' type='invalid' className='d-block mt-0 mb-1'>
                                {formState.errors.vat.message}
                            </Form.Control.Feedback>
                        </td>
                    </tr>
                )}
                </>
            )}
            {watch('ddc') && !isCompany && canBeVatRegistered && (
                // ДДС номерът на физическо лице е самото ЕГН/ЛНЧ/БУЛСТАТ - показва се, но не се въвежда.
                <NewRequestTableRow label={t('vat')} id='vat' editForm={editForm}>
                    <p className='mb-0 mx-1'>BG</p>
                    <div className='flex-grow-1'>
                        <Form.Control type='text' id='person-vat' value={digitsOnly(eik)} readOnly disabled />
                    </div>
                </NewRequestTableRow>
            )}
            <NewRequestTableRow
                label={t('emailForCorrespondence')}
                id='email'
                editForm={editForm}
                required={formState.errors.email}
            >
                <Controller
                    name='email'
                    {...{ control }}
                    rules={{
                        validate: (value) => {
                            if (!value) return true;
                            const emails = value.split(/[,;\s]+/).map(email => email.trim()).filter(Boolean);
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            const invalid = emails.filter((e) => !emailRegex.test(e));
                            if (invalid.length > 0) {
                                return `${t('invalidEmail') || 'Невалиден имейл адрес'}: ${invalid.join(', ')}`;
                            }
                            return true;
                        },
                    }}
                    render={({ field }) => (
                        <EmailChipInput
                            id='email'
                            name={field.name}
                            value={field.value ?? ''}
                            placeholder={t('emailsPlaceholder') || 'напр. office@client.com, acc@client.com'}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            isInvalid={!!formState.errors.email}
                        />
                    )}
                ></Controller>
            </NewRequestTableRow>
            <NewRequestTableRow
                label={t('invoiceText')}
                id='invoiceText'
                inputContainerClass='justify-content-center'
            >
                <Controller
                    name='invoiceTextTemplateId'
                    {...{ control }}
                    render={({ field }) => (
                        <Select<{ value: string; label: string }, false>
                            isDisabled={disabled || templates.length === 0}
                            isLoading={!templatesError && templates.length === 0}
                            className={'w-100'}
                            value={invoiceTextOptions.find((option) => option.value === selectedId) ?? null}
                            styles={optionSelectStyles}
                            placeholder={t('invoiceText')}
                            options={invoiceTextOptions}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            onChange={(option) => field.onChange(option?.value ?? '')}
                        />
                    )}
                />
                {templatesError && <span className='text-danger'>{templateT('formLoadError')}</span>}
            </NewRequestTableRow>
            {selectedTemplate?.template.includes('{period}') && <NewRequestTableRow
                id='hasDays'
                label={t('hasDays')}
                inputContainerClass='justify-content-center'
                labelContainerClass='w-50'
            >
                    <Controller
                        name='hasDays'
                        {...{ control }}
                        render={({ field }) => (
                            <Form.Check
                                disabled={disabled}
                                checked={field.value || false}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </NewRequestTableRow>}
                <NewRequestTableRow
                    id='documentNumber'
                    label={t('documentNumber')}
                    inputContainerClass='justify-content-center'
                    required={formState.errors.documentNumber}
                >
                    <Controller
                        name='documentNumber'
                        {...{ control }}
                        rules={{ validate: value => !selectedTemplate?.template.includes('{documentNumber}') || !!value?.trim() }}
                        render={({ field }) => (
                            <Form.Control
                                type='text'
                                value={field.value || ''}
                                ref={field.ref}
                                name={field.name}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                            />
                        )}
                    ></Controller>
                </NewRequestTableRow>
        </>
    );
};
